import * as React from "react";
import axios from "axios";
import {getHeadersConfig} from "../utils/config";
import {DataGridPanel} from "../datagrid/DataGridPanel";
import FiltersPanel from "../panels/FiltersPanel";
import "bootstrap/dist/css/bootstrap.min.css";
import ErrorHandler from "../utils/ErrorHandler";
import {PageName,ColumnIds,ProductsCampaignsDto,DataType,TrafficSourceResponseDto} from "../utils/types";
import {PlacementResponseDto,SearchResponseDto,ResponseStatus} from "../utils/types";
import {datagridColumns, datagridNumericWidth,datagridPercentageWidth,styleCategorical} from "../utils/datagridCommon";
import {PagesHelper} from "../common/PagesHelper";
import StateHelper from "../utils/StateHelper";
import { FiltersHelper } from "../common/FiltersHelper";
import { joinURLs } from "../utils/urlUtil";
import { TrafficSources } from "../services/TrafficSources";

let abortController = new AbortController();

const loadPerformancePageColumns = () => {
  datagridColumns.set(ColumnIds.productID, { id: ColumnIds.productID, visible: true, name: "Product ID", showQuickFilterMenu : true,align: "left", isGroupByColumn: true, type: DataType.STRING,style: { minWidth: 100, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }});
  datagridColumns.set(ColumnIds.campaignID, { id: ColumnIds.campaignID, visible: true, name: "AppTap Campaign ID", showQuickFilterMenu : true, isGroupByColumn: true, align: "left",type: DataType.STRING,style: { minWidth: 100, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }});
  datagridColumns.set(ColumnIds.campaignSource, { id: ColumnIds.campaignSource, visible: true, name: "Campaign Source", showQuickFilterMenu : true, isGroupByColumn: true, align: "left",type: DataType.STRING,style: { minWidth: 100, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }});
  datagridColumns.set(ColumnIds.campaignSourceAlias, { id: ColumnIds.campaignSourceAlias, visible: true, name: "Campaign Source Alias", showQuickFilterMenu : true, isGroupByColumn: true, align: "left",type: DataType.STRING,style: { minWidth: 100, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }});
  datagridColumns.set(ColumnIds.productViews, { id: ColumnIds.productViews, visible: true, name: "Product Views", showQuickFilterMenu : false, isGroupByColumn: false, align: "right", type: DataType.NUMBER, style: datagridNumericWidth });

  //performance reports: connexity columns
  datagridColumns.set(ColumnIds.demandAccountID, {id: ColumnIds.demandAccountID, name: "Demand Account ID", visible: true, showQuickFilterMenu : false, align: "left", isGroupByColumn: true, type: DataType.STRING,style: { ...styleCategorical, minWidth: 100, maxWidth: 150}});
  datagridColumns.set(ColumnIds.demandPlacementID, {id: ColumnIds.demandPlacementID, name: "Demand Placement ID", visible: true, showQuickFilterMenu : false, isGroupByColumn: true, align: "left",type: DataType.STRING,style: { ...styleCategorical, minWidth: 100, maxWidth: 300}});
  datagridColumns.set(ColumnIds.postConversionEvents, {id: ColumnIds.postConversionEvents, name: "Post Conversion Events", visible: true , showQuickFilterMenu : false, isGroupByColumn: false, align: "right", type: DataType.NUMBER, style: datagridNumericWidth });
  datagridColumns.set(ColumnIds.postConversionEventRate, {id: ColumnIds.postConversionEventRate, name: "Post Conversion Events Rate", avgDivideByNumDate: true, visible: true , showQuickFilterMenu : false, isGroupByColumn: false, align: "right", type: DataType.PERCENTAGE, precision: 2, style: datagridPercentageWidth });

  //performance reports: amazon
  datagridColumns.set(ColumnIds.productItemID, {id: ColumnIds.productItemID, name: "Product Item ID", visible: true, showQuickFilterMenu : false, align: "left", isGroupByColumn: true, type: DataType.STRING,style: { ...styleCategorical, minWidth: 100, maxWidth: 150}});
  datagridColumns.set(ColumnIds.productItemName, {id: ColumnIds.productItemName, name: "Product Item Name", visible: true, showQuickFilterMenu : false, align: "left", isGroupByColumn: true, type: DataType.STRING,style: { ...styleCategorical, minWidth: 100, maxWidth: 150}});
};

const clear = (setSearchResponseDto:any, setActualPage:any) => {
  setSearchResponseDto(null);
  setActualPage(1);    
}

const pageSearch = (currentUser:any, setSearchResponseDto:any, setActualPage:any, updateHistory:boolean = true) => {
  if (abortController) abortController.abort();
  abortController = new AbortController();

  FiltersHelper.checkRequiredGroupByAndPermissions(currentUser, PageName.performanceReports);
  if (updateHistory) PagesHelper.historyPushFromPageState();

  const queryParams = PagesHelper.getQueryParameters(PageName.performanceReports);
  const config = getHeadersConfig();
  const apiUrl = joinURLs("2/performance/demand", process.env.REACT_APP_REPORTING_API_BASE_URL);

  const responseDto : SearchResponseDto = {
    status : ResponseStatus.LOADING, 
    error : null,
    data : null,
    query : null,
    metadata : null
  };
  setSearchResponseDto(responseDto);
  
  axios
    .get(apiUrl, {...config, params: queryParams, signal: abortController.signal })
    .then(function (response: any) {

      if (response.data.data.length > 0) {

        const responseDto : SearchResponseDto = response.data;
        responseDto.status = ResponseStatus.OK; 
        responseDto.error = null;          
        setSearchResponseDto(response.data);
        setActualPage(1);

      } else {
        clear(setSearchResponseDto, setActualPage);
      }
    })
    .catch(function (err) {
      // handle error
      if (!axios.isCancel(err)) {
        clear(setSearchResponseDto, setActualPage);
        const isLoggedOut : boolean = ErrorHandler.handleLoggedOutError(err);
        if(! isLoggedOut){
          const responseDto : SearchResponseDto = {
            status : ResponseStatus.ERROR, 
            error : err?.response?.data?.errorMessage,           
            data : null,
            query :  null,
            metadata : null
          };
          setSearchResponseDto(responseDto);
        }
      }
    });
};

function PerformanceReportsPage(props : any) {
  const [searchResponseDto, setSearchResponseDto] = React.useState<SearchResponseDto>(null);
  const [actualPage,setActualPage] = React.useState(1);
  const [trafficSourceResponseDto, setTrafficSourceResponseDto] = React.useState<TrafficSourceResponseDto>(null);
  const [placementsResponseDto, setPlacementsResponseDto] = React.useState<PlacementResponseDto>(null);
  const [placementsIsLoading, setPlacementsIsLoading] = React.useState<boolean>(false);
  const [productsCampaignsDto, setProductsCampaignsDto] = React.useState<ProductsCampaignsDto>(null);

  loadPerformancePageColumns();
  function search(updateHistory:boolean = true) {
    pageSearch(props.currentUser, setSearchResponseDto, setActualPage, updateHistory);
  }

  StateHelper.setSearchFuntion(PageName.performanceReports, search );

  React.useEffect(() => {
    if (props.currentUser) {
      TrafficSources.getTrafficSources(setTrafficSourceResponseDto);
      PagesHelper.loadProductsAndCampaignsHelper(PageName.performanceReports, true, setProductsCampaignsDto);
      pageSearch(props.currentUser, setSearchResponseDto, setActualPage, false);
    }

    return () => {
      if (abortController) abortController.abort();
    };
  }, [props.currentUser]);

  return (
    <>
      <FiltersPanel 
        search={search} 
        page={PageName.performanceReports} 

        placementsIsLoading={placementsIsLoading}
        setPlacementsIsLoading={setPlacementsIsLoading}
        
        placementsResponseDto={placementsResponseDto}
        setPlacementsResponseDto={setPlacementsResponseDto}

        currentUser={props.currentUser}

        trafficSourceResponseDto={trafficSourceResponseDto}

        productsCampaignsDto={productsCampaignsDto}
      />
      <hr/>
      <DataGridPanel 
        search={search}

        page={PageName.performanceReports} 

        searchResponseDto={searchResponseDto} 
        setSearchResponseDto={setSearchResponseDto}

        datagridColumns={datagridColumns} 
        setActualPage={setActualPage} 
        actualPage={actualPage} 

        currentUser={props.currentUser}

        trafficSourceResponseDto={trafficSourceResponseDto}
        setTrafficSourceResponseDto={setTrafficSourceResponseDto}

        productsCampaignsDto={productsCampaignsDto}
        setProductsCampaignsDto={setProductsCampaignsDto}
      />
    </>
  );
}

export default PerformanceReportsPage;
